import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import ConfirmationModal from './ConfirmationModal'
import { X, Mail, User, Copy, ExternalLink, Check } from 'lucide-react'
import hubUserInvitationsAPI from '../../services/hubUserInvitationsAPI'
import { rolesAPI } from '../../services/rolesAPI'
import toast from 'react-hot-toast'

export default function InviteHubUserModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role_id: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createdInvitation, setCreatedInvitation] = useState(null)
  const [copied, setCopied] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const queryClient = useQueryClient()

  // Fetch hub roles (control_center) from product_roles table
  const { data: rolesData, isLoading: rolesLoading } = useQuery(
    ['hub-roles'],
    () => rolesAPI.getAll({ user_type: 'control_center' }),
    { enabled: isOpen }
  )
  const roles = rolesData?.data?.data?.roles || []
  
  // Debug: Log roles data
  useEffect(() => {
    if (isOpen && rolesData) {
      console.log('Roles data:', rolesData)
      console.log('Roles array:', roles)
    }
  }, [isOpen, rolesData, roles])

  const createInvitationMutation = useMutation(
    (invitationData) => hubUserInvitationsAPI.create(invitationData),
    {
      onError: (error) => {
        console.error('Error creating invitation:', error)
        const errorMsg = error.response?.data?.message || 'Failed to create invitation'
        setErrorMessage(errorMsg)
        setShowErrorModal(true)
      }
    }
  )

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role_id: ''
      })
      setCreatedInvitation(null)
      setCopied(false)
    }
  }, [isOpen])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      toast.success('Invitation link copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {
      toast.error('Failed to copy link')
    })
  }

  const openInBrowser = (url) => {
    window.open(url, '_blank')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setCreatedInvitation(null)

    try {
      // Combine first_name and last_name into full_name
      const full_name = [formData.first_name, formData.last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || null

      const result = await createInvitationMutation.mutateAsync({
        email: formData.email.trim(),
        full_name: full_name,
        role_id: formData.role_id || null,
        custom_message: null
      })

      setCreatedInvitation({
        email: formData.email.trim(),
        invitationLink: result.data.invitationLink,
        emailSent: result.data.emailSent
      })

      toast.success('Invitation created successfully!')
      queryClient.invalidateQueries(['sas-users-all'])
      if (onSuccess) onSuccess()
    } catch (error) {
      // Error already handled in mutation
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role_id: ''
      })
      setCreatedInvitation(null)
      setCopied(false)
      setShowErrorModal(false)
      setErrorMessage('')
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose()
        }
      }}
    >
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Mail className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>Invite Hub User</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {createdInvitation ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-lg font-medium text-green-900 mb-2">
                  Invitation Created Successfully!
                </h3>
                <p className="text-sm text-green-700">
                  The invitation has been created.
                  {createdInvitation.emailSent && (
                    <span> An email has been sent to {createdInvitation.email}.</span>
                  )}
                </p>
              </div>

              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{createdInvitation.email}</span>
                  {createdInvitation.emailSent && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                      Email Sent
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="text"
                    value={createdInvitation.invitationLink}
                    readOnly
                    className="flex-1 input text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(createdInvitation.invitationLink)}
                    className="flex-shrink-0"
                    title="Copy link"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openInBrowser(createdInvitation.invitationLink)}
                    className="flex-shrink-0"
                    title="Open in browser"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button onClick={handleClose}>
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                {/* First Name and Last Name in same row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="input w-full"
                      placeholder="Enter first name"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="input w-full"
                      placeholder="Enter last name"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input w-full"
                    placeholder="user@example.com"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Role *
                  </label>
                  {rolesLoading ? (
                    <div className="flex items-center justify-center py-2">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : (
                    <select
                      name="role_id"
                      value={formData.role_id}
                      onChange={handleInputChange}
                      className="input w-full"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select a role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || rolesLoading}
                  className="min-w-[120px]"
                >
                  {isSubmitting ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <User className="h-4 w-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Error Modal */}
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false)
          setErrorMessage('')
        }}
        onConfirm={() => {
          setShowErrorModal(false)
          setErrorMessage('')
        }}
        title="Error Creating Invitation"
        message={errorMessage || 'Failed to create invitation. Please try again.'}
        confirmText="Got it"
        cancelText={null}
        type="warning"
      />
    </div>
  )
}
